/**
 * Utility functions for computing challenge statistics and leaderboards
 */

import {
  Challenge,
  Prediction,
  ChallengeWithStats,
  ParticipantStats,
  User,
  Match
} from '../types/challenges';
import { formatTime12h, formatWeekday, formatShortDate } from './dateFormatters.helper';

/**
 * Calculate total points for a user in a challenge
 */
export const calculateUserPoints = (
  predictions: Prediction[],
  userId: string,
  challengeId: string
): number => {
  return predictions
    .filter(p => p.userId === userId && p.challengeId === challengeId && p.points !== undefined)
    .reduce((sum, p) => sum + (p.points || 0), 0);
};

/** Minimal user shape for participant stats (name or username optional). */
export type ParticipantUser = Pick<User, 'id'> & { name?: string; username?: string; avatar?: string };

/**
 * Get participant statistics for a challenge
 */
export const getParticipantStats = (
  challenge: Challenge,
  predictions: Prediction[],
  users: ParticipantUser[]
): ParticipantStats[] => {
  const stats: ParticipantStats[] = challenge.participantIds.map(userId => {
    const user = users.find(u => u.id === userId);
    const userPredictions = predictions.filter(
      p => p.userId === userId && p.challengeId === challenge.id
    );

    const totalPoints = userPredictions
      .filter(p => p.points !== undefined)
      .reduce((sum, p) => sum + (p.points || 0), 0);

    let userName = 'Unknown';
    if (user) {
      if (user.name) {
        userName = user.name;
      } else if ('firstName' in user || 'lastName' in user) {
        const userWithNames = user as { firstName?: string; lastName?: string; username?: string };
        userName = `${userWithNames.firstName || ''} ${userWithNames.lastName || ''}`.trim() || userWithNames.username || 'Unknown';
      } else {
        userName = user.username || 'Unknown';
      }
    }

    return {
      userId,
      userName,
      avatar: user?.avatar,
      totalPredictions: userPredictions.length,
      totalPoints,
      rank: 0,
      predictions: userPredictions,
    };
  });

  // Deterministic tie-break: totalPoints desc, then totalPredictions (correct) desc, then userId asc (matches server list rank)
  stats.sort((a, b) => {
    if (b.totalPoints !== a.totalPoints) return b.totalPoints - a.totalPoints;
    const aCorrect = a.predictions.filter((p) => p.correct === true).length;
    const bCorrect = b.predictions.filter((p) => p.correct === true).length;
    if (bCorrect !== aCorrect) return bCorrect - aCorrect;
    return String(a.userId).localeCompare(String(b.userId));
  });
  stats.forEach((stat, index) => {
    stat.rank = index + 1;
  });

  return stats;
};

/**
 * Enhance a challenge with computed statistics
 */
export const enhanceChallengeWithStats = (
  challenge: Challenge,
  predictions: Prediction[],
  users: User[],
  currentUserId?: string
): ChallengeWithStats => {
  const participantStats = getParticipantStats(challenge, predictions, users);
  const leader = participantStats.length > 0 ? participantStats[0] : undefined;

  let myRank: number | undefined;
  let myPoints: number | undefined;

  if (currentUserId) {
    const myStats = participantStats.find(s => s.userId === currentUserId);
    if (myStats) {
      myRank = myStats.rank;
      myPoints = myStats.totalPoints;
    }
  }

  return {
    ...challenge,
    participantCount: challenge.participantIds.length,
    matchCount: challenge.matchIds.length,
    leader: leader ? {
      userId: leader.userId,
      userName: leader.userName,
      points: leader.totalPoints,
    } : undefined,
    myRank,
    myPoints,
  };
};

/**
 * Get all challenges with stats for a user
 */
export const getUserChallengesWithStats = (
  challenges: Challenge[],
  predictions: Prediction[],
  users: User[],
  userId: string
): ChallengeWithStats[] => {
  const userChallenges = challenges.filter(c =>
    c.participantIds.includes(userId) && c.status === 'active'
  );

  return userChallenges.map(challenge =>
    enhanceChallengeWithStats(challenge, predictions, users, userId)
  );
};

/**
 * Get user's prediction for a specific match in a challenge
 */
export const getUserPredictionForMatch = (
  predictions: Prediction[],
  challengeId: string,
  userId: string,
  matchId: string
): Prediction | undefined => {
  return predictions.find(
    p => p.challengeId === challengeId &&
         p.userId === userId &&
         p.matchId === matchId
  );
};

/**
 * Calculate points for a prediction based on actual match result.
 * @deprecated Points are now computed and persisted by the backend settlement service. Use API pointsEarned instead.
 */
export const calculatePredictionPoints = (
  prediction: Prediction,
  match: Match,
  scoringRules: Challenge['scoringRules']
): number => {
  if (match.status !== 'finished' || match.homeScore === undefined || match.awayScore === undefined) {
    return 0;
  }

  if (prediction.homeScore === match.homeScore && prediction.awayScore === match.awayScore) {
    return scoringRules.exactScore;
  }

  if (prediction.outcome === match.finalOutcome) {
    return scoringRules.correctOutcome;
  }

  return 0;
};

/**
 * Get prediction status display (for UI).
 * @deprecated Status/points now come from API (pointsEarned, isCorrectOutcome, isExactScore). Use API data instead.
 */
export const getPredictionStatus = (prediction: Prediction, match: Match): {
  status: 'pending' | 'correct' | 'partial' | 'incorrect';
  points: number;
  color: string;
} => {
  if (match.status !== 'finished') {
    return { status: 'pending', points: 0, color: '#FFA500' };
  }

  const points = prediction.points || 0;

  if (points === 0) {
    return { status: 'incorrect', points: 0, color: '#FF4444' };
  } else if (prediction.homeScore === match.homeScore && prediction.awayScore === match.awayScore) {
    return { status: 'correct', points, color: '#4CAF50' };
  } else {
    return { status: 'partial', points, color: '#FFA500' };
  }
};

/**
 * Format date for display
 */
export const formatMatchDate = (date: Date): string => {
  const now = new Date();
  const matchDate = new Date(date);
  const diffMs = matchDate.getTime() - now.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays === 0 && diffHours >= 0) {
    return `Today at ${formatTime12h(matchDate)}`;
  } else if (diffDays === 1) {
    return `Tomorrow at ${formatTime12h(matchDate)}`;
  } else if (diffDays > 1 && diffDays < 7) {
    return `${formatWeekday(matchDate)} at ${formatTime12h(matchDate)}`;
  } else {
    return formatShortDate(matchDate);
  }
};

/**
 * Check if user has completed all predictions for a challenge
 */
export const hasCompletedAllPredictions = (
  challenge: Challenge,
  predictions: Prediction[],
  userId: string
): boolean => {
  const userPredictions = predictions.filter(
    p => p.challengeId === challenge.id && p.userId === userId
  );
  return userPredictions.length === challenge.matchIds.length;
};

/**
 * Get completion percentage for a challenge
 */
export const getChallengeCompletionPercentage = (
  challenge: Challenge,
  predictions: Prediction[],
  userId: string
): number => {
  const userPredictions = predictions.filter(
    p => p.challengeId === challenge.id && p.userId === userId
  );
  return Math.round((userPredictions.length / challenge.matchIds.length) * 100);
};
